import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { contentService, moduleService, sitesService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Heart, Clock, ChevronRight, Sparkles, XCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

// Kategori renkleri
const CATEGORY_COLORS = {
  ev_arac: { bg: 'bg-blue-500', text: 'text-blue-500', light: 'bg-blue-500/10', border: 'border-blue-500/30' },
  sarj_teknoloji: { bg: 'bg-orange-500', text: 'text-orange-500', light: 'bg-orange-500/10', border: 'border-orange-500/30' },
  gunes_enerjisi: { bg: 'bg-yellow-500', text: 'text-yellow-500', light: 'bg-yellow-500/10', border: 'border-yellow-500/30' },
  enerji_depolama: { bg: 'bg-green-500', text: 'text-green-500', light: 'bg-green-500/10', border: 'border-green-500/30' },
  genel: { bg: 'bg-purple-500', text: 'text-purple-500', light: 'bg-purple-500/10', border: 'border-purple-500/30' },
};

const CATEGORY_LABELS = {
  ev_arac: 'Elektrikli Araçlar',
  sarj_teknoloji: 'Şarj Teknolojileri',
  gunes_enerjisi: 'Güneş Enerjisi',
  enerji_depolama: 'Enerji Depolama',
  genel: 'Genel',
};

// Placeholder görsel
const DEFAULT_COVER = 'https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=800&auto=format&fit=crop&q=60';

// Handle image URL - support both /uploads/ paths and full URLs
const getImageUrl = (url) => {
  if (!url) return DEFAULT_COVER;
  if (url.startsWith('/uploads/')) {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    return `${API_URL}${url}`;
  }
  return url;
};

// Featured Article Component
const FeaturedArticle = ({ article, onLike, userId }) => {
  const navigate = useNavigate();
  const colors = CATEGORY_COLORS[article.category] || CATEGORY_COLORS.genel;
  const isLiked = article.liked_by?.includes(userId);

  return (
    <div 
      className="relative w-full h-[400px] rounded-3xl overflow-hidden cursor-pointer group"
      onClick={() => navigate(`/news/${article.id}`)}
    >
      {/* Background Image */}
      <img 
        src={getImageUrl(article.cover_image)} 
        alt={article.title}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        style={{ objectFit: 'cover', objectPosition: 'center' }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
      
      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
        {/* Category Badge */}
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${colors.bg} text-white mb-4`}>
          <Sparkles className="w-3 h-3 mr-1" />
          {CATEGORY_LABELS[article.category]}
        </span>
        
        {/* Title */}
        <h1 className="text-2xl md:text-4xl font-bold text-white mb-3 line-clamp-2 group-hover:text-blue-300 transition-colors">
          {article.title}
        </h1>
        
        {/* Summary */}
        <p className="text-gray-300 text-sm md:text-base mb-4 line-clamp-2 max-w-3xl">
          {article.summary}
        </p>
        
        {/* Meta */}
        <div className="flex items-center gap-6 text-gray-400 text-sm">
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {article.read_time} dk okuma
          </span>
          <button 
            onClick={(e) => { e.stopPropagation(); onLike(article.id); }}
            className={`flex items-center gap-1 transition-all ${isLiked ? 'text-red-500' : 'hover:text-red-400'}`}
          >
            <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
            {article.likes_count}
          </button>
          <span className="text-gray-500">|</span>
          <span>{article.author_name}</span>
        </div>
      </div>
      
      {/* Read More Arrow */}
      <div className="absolute bottom-6 right-6 md:bottom-10 md:right-10 w-12 h-12 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center group-hover:bg-blue-500 transition-all">
        <ChevronRight className="w-6 h-6 text-white" />
      </div>
    </div>
  );
};

// Article Card Component
const ArticleCard = ({ article, onLike, userId }) => {
  const navigate = useNavigate();
  const colors = CATEGORY_COLORS[article.category] || CATEGORY_COLORS.genel;
  const isLiked = article.liked_by?.includes(userId);

  return (
    <div 
      className="group bg-card border border-border rounded-2xl overflow-hidden cursor-pointer hover:-translate-y-2 hover:shadow-2xl hover:shadow-primary/10 transition-all duration-300"
      onClick={() => navigate(`/news/${article.id}`)}
    >
      {/* Cover Image */}
      <div className="relative h-48 overflow-hidden">
        <img 
          src={getImageUrl(article.cover_image)} 
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        {/* Category Badge */}
        <span className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold ${colors.bg} text-white`}>
          {CATEGORY_LABELS[article.category]}
        </span>
      </div>
      
      {/* Content */}
      <div className="p-5">
        <h3 className="font-semibold text-lg text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {article.title}
        </h3>
        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
          {article.summary}
        </p>
        
        {/* Footer */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-3 text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {article.read_time} dk
            </span>
          </div>
          
          <button 
            onClick={(e) => { e.stopPropagation(); onLike(article.id); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all ${
              isLiked 
                ? 'bg-red-500/10 text-red-500' 
                : 'bg-muted hover:bg-red-500/10 hover:text-red-500 text-muted-foreground'
            }`}
          >
            <Heart className={`w-4 h-4 transition-transform ${isLiked ? 'fill-current scale-110' : 'group-hover:scale-110'}`} />
            <span className="font-medium">{article.likes_count}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Category Filter Pills
const CategoryFilter = ({ selected, onChange }) => {
  const categories = [
    { value: 'all', label: 'Tümü', color: 'purple' },
    { value: 'ev_arac', label: 'EV', color: 'blue' },
    { value: 'sarj_teknoloji', label: 'Şarj', color: 'orange' },
    { value: 'gunes_enerjisi', label: 'Güneş', color: 'yellow' },
    { value: 'enerji_depolama', label: 'Enerji', color: 'green' },
    { value: 'genel', label: 'Genel', color: 'gray' },
  ];

  return (
    <div className="flex flex-wrap gap-2">
      {categories.map(cat => (
        <button
          key={cat.value}
          onClick={() => onChange(cat.value)}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            selected === cat.value
              ? `bg-${cat.color}-500 text-white shadow-lg shadow-${cat.color}-500/25`
              : 'bg-muted text-muted-foreground hover:bg-accent'
          }`}
          style={selected === cat.value ? { 
            backgroundColor: cat.color === 'blue' ? '#3b82f6' : 
                           cat.color === 'orange' ? '#f97316' :
                           cat.color === 'yellow' ? '#eab308' :
                           cat.color === 'green' ? '#22c55e' :
                           cat.color === 'purple' ? '#a855f7' : '#6b7280'
          } : {}}
        >
          {cat.label}
        </button>
      ))}
    </div>
  );
};

// Main News Page
const NewsPage = () => {
  const { user, isSuperOrAdmin } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [moduleEnabled, setModuleEnabled] = useState(true);
  const [checkingModule, setCheckingModule] = useState(true);

  // Modül durumunu kontrol et
  useEffect(() => {
    const checkModuleStatus = async () => {
      try {
        let siteId = user?.site_id;
        
        // Admin için ilk site'ı al
        if (!siteId && isSuperOrAdmin()) {
          const sites = await sitesService.list();
          if (sites && sites.length > 0) {
            siteId = sites[0].id;
          }
        }
        
        if (siteId) {
          const config = await moduleService.get(siteId);
          setModuleEnabled(config.news_center !== false);
        }
      } catch (error) {
        console.error('Module check error:', error);
        setModuleEnabled(true); // Hata durumunda göster
      } finally {
        setCheckingModule(false);
      }
    };
    
    if (user) {
      checkModuleStatus();
    }
  }, [user, isSuperOrAdmin]);

  useEffect(() => {
    if (!checkingModule && moduleEnabled) {
      loadArticles();
    }
  }, [category, checkingModule, moduleEnabled]);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const params = { published_only: true, limit: 50 };
      if (category !== 'all') {
        params.category = category;
      }
      const response = await contentService.list(params);
      setArticles(response.items || []);
    } catch (error) {
      toast.error('Haberler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (articleId) => {
    try {
      const response = await contentService.toggleLike(articleId);
      // Update local state
      setArticles(prev => prev.map(article => {
        if (article.id === articleId) {
          return {
            ...article,
            likes_count: response.likes_count,
            liked_by: response.user_liked 
              ? [...(article.liked_by || []), user.id]
              : (article.liked_by || []).filter(id => id !== user.id)
          };
        }
        return article;
      }));
    } catch (error) {
      toast.error('Beğeni işlemi başarısız');
    }
  };

  const featuredArticle = articles[0];
  const gridArticles = articles.slice(1);

  // Modül kontrolü yapılıyor
  if (checkingModule) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Modül kapalı
  if (!moduleEnabled) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-8">
        <XCircle className="w-16 h-16 text-muted-foreground mb-4" />
        <h2 className="text-2xl font-bold text-foreground mb-2">Haber Merkezi Kapalı</h2>
        <p className="text-muted-foreground max-w-md">
          Bu modül şu anda devre dışı bırakılmış. Modül yönetimi sayfasından aktifleştirebilirsiniz.
        </p>
        <button
          onClick={() => navigate('/modules')}
          className="mt-6 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          Modül Yönetimine Git
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="news-page">
      {/* Header with Back Button */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          {/* Geri Butonu - Teknisyen için teknisyen paneline, diğerleri için dashboard'a */}
          <button
            onClick={() => navigate(user?.role === 'calisan' ? '/technician' : '/dashboard')}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Geri</span>
          </button>
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Sparkles className="w-8 h-8 text-primary" />
              Haber Merkezi
            </h1>
            <p className="text-muted-foreground mt-1">
              Elektrikli araç ve enerji dünyasından son haberler
            </p>
          </div>
        </div>
        
        {/* Category Filters */}
        <CategoryFilter selected={category} onChange={setCategory} />
      </div>

      {/* Featured Article */}
      {featuredArticle && (
        <FeaturedArticle 
          article={featuredArticle} 
          onLike={handleLike}
          userId={user?.id}
        />
      )}

      {/* Article Grid */}
      {gridArticles.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {gridArticles.map(article => (
            <ArticleCard 
              key={article.id} 
              article={article} 
              onLike={handleLike}
              userId={user?.id}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {articles.length === 0 && (
        <div className="text-center py-16">
          <Sparkles className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold text-foreground mb-2">Henüz haber yok</h3>
          <p className="text-muted-foreground">Bu kategoride yayınlanmış içerik bulunmuyor.</p>
        </div>
      )}
    </div>
  );
};

export default NewsPage;
